// apps/share-web/src/offline/setlist-offline.ts
//
// Чистые помощники докачки СОСТАВА предстоящих сетлистов (задача C5, волна
// G): что считается «предстоящим», устарела ли закэшированная копия, и какие
// id слов унести в докачку слов (offline/prefetch.ts, `prefetchWords`).
// Ни сети, ни хранилища, ни DOM здесь нет — цикл докачки живёт в
// offline/prefetch.ts, эти функции только решают.
//
// СЕТЛИСТЫ СО ЗВУКОМ (`TeamLibrary.setlists`) И СЛОВА-ТОЛЬКО СТРОКИ
// (`textSetlists`, задача D4). Состав тянется с `GET /api/setlists/:id`
// (fetchPublishedSetlist, ../setlist.ts), а у этого маршрута один вход —
// `setlistId`. `SetlistSummary` его несёт всегда; `TextSetlistSummary`
// (слова-только строки вкладки «Сетлисты») — только когда воркер нашёл
// сетлист того же (владелец, local_setlist_id) СВОЕЙ же команды, не отозванный
// (team-setlists-merge.ts). Строка без него (легаси-ссылка до моста фазы 2,
// сетлист другой команды владельца, отозванный сетлист) в докачку не идёт —
// докачивать нечего, ссылка живёт страницей `/t/<uuid>` без офлайн-состава.
//
// Дедуп по `setlistId`, а не по факту происхождения строки: воркер уже не
// кладёт в `textSetlists[]` то, что склеилось с `setlists[]` того же ключа
// (тот же (владелец, local) не может дать ДВЕ разные строки с ОДНИМ и тем же
// `setlistId`), но повторная проверка здесь — тот же принцип «граница
// проверяется в двух местах», что и у воркера (см. его комментарии).
//
// `updatedAt` у слов-только строки взять неоткуда — `TextSetlistSummary` его
// не несёт (`text_shares`/мост фазы 2 не подставляют `published_setlists`
// `updated_at`). `createdAt` — лучшее, что есть: стабилен, докачка не
// перескачивает то, что уже лежит в кэше под тем же снимком, но не увидит
// правку состава без смены `createdAt` (у слов-только строки его и не бывает
// — известное ограничение, не эта задача).

import type { TeamLibrary, SetlistSummary } from '../library.js';
import type { CachedSetlist } from './offline-store.js';
import { splitAndSortSetlists } from '../ui/setlist-sort.js';

/**
 * Ключ офлайн-кэша ОДНОГО сетлиста (kind `'setlist'`) — ВСЕГДА эта константа,
 * НИКОГДА реальный `teamId` (исправление задачи D4: до этой правки
 * `prefetchSetlists` писал под настоящей командой, а экран `/s/<uuid>`
 * (`mountGuestSetlistScreen`, setlist-screen/route.ts) читал под `''` —
 * докачанное заранее никогда не находилось офлайн).
 *
 * `setlistId` глобально уникален (UUID, минтится воркером) — коллизии между
 * командами ему не грозят, а команда экрану ДО успешного ответа сервера не
 * известна: гость читает по прямой ссылке без входа, участник — тоже, пока
 * не решит доступ сервер. Team-scoped ключ обязал бы читателя знать команду
 * РАНЬШЕ того единственного ответа, который её и сообщает — то есть ровно
 * тогда, когда сети нет и спросить сервер уже нельзя.
 *
 * Единственный источник этой константы — используется офлайн/prefetch.ts
 * (`prefetchSetlists`), setlist-screen/route.ts (`mountGuestSetlistScreen`) и
 * offline/reconcile.ts (сверка) — второй копии `''` в кодовой базе быть не
 * должно.
 */
export const SETLIST_KEY_TEAM = '';

/** Один предстоящий сетлист: команда, которой он принадлежит в библиотеке
 *  (для отчётности/группировки — НЕ для адреса в хранилище, см.
 *  `SETLIST_KEY_TEAM` выше), и сводка библиотеки, которой сверяется
 *  устаревание. */
export interface UpcomingSetlistRef {
  teamId: string;
  summary: SetlistSummary;
}

/**
 * Предстоящие сетлисты СО ЗВУКОМ всех команд — тем же правилом, что у
 * группы «Upcoming» библиотеки (`splitAndSortSetlists`, ../ui/setlist-sort.ts):
 * не копия правила, тот же вызов, чтобы офлайн-докачка никогда не разошлась с
 * тем, что человек видит на экране библиотеки («предстоящий» здесь и там —
 * одно и то же понятие).
 *
 * `now` — параметр, а не `new Date()` внутри: тем же приёмом, что `nowMs` у
 * `freshness.ts` — иначе границу «сегодня» пришлось бы тестировать подменой
 * системных часов.
 */
export function upcomingSetlistIds(library: TeamLibrary[], now: Date): UpcomingSetlistRef[] {
  const refs: UpcomingSetlistRef[] = [];
  for (const team of library) {
    const audio = team.setlists ?? [];
    const seen = new Set(audio.map((s) => s.setlistId));
    const words: SetlistSummary[] = [];
    for (const w of team.textSetlists ?? []) {
      if (!w.setlistId || seen.has(w.setlistId)) continue;
      seen.add(w.setlistId);
      words.push({
        setlistId: w.setlistId, name: w.name, ownerEmail: w.ownerEmail, itemCount: w.itemCount,
        createdAt: w.createdAt, updatedAt: w.createdAt, eventDate: w.eventDate, textShareId: w.textShareId,
      });
    }
    // Тот же `splitAndSortSetlists`, что группирует «Upcoming»/«Past» на
    // экране библиотеки (ui/library-view.ts, поверх `librarySetlistEntries`)
    // — не копия правила, тот же вызов на объединённом списке, чтобы граница
    // «предстоящий» здесь и там не разошлась.
    const { upcoming } = splitAndSortSetlists([...audio, ...words], now);
    for (const summary of upcoming) refs.push({ teamId: team.teamId, summary });
  }
  return refs;
}

/**
 * Устарела ли закэшированная копия состава относительно того, что сейчас
 * говорит библиотека. Отсутствие кэша — тоже устаревание: докачка обязана
 * скачать состав в первый раз. Сверка — по `updatedAt` библиотеки
 * (`SetlistSummary.updatedAt`), не по телу самого ответа: тем же приёмом, что
 * у слов (`SongTextSummary.updatedAt`, offline/prefetch.ts) — сверка версий
 * не стоит ни одного запроса.
 */
export function isStale(cached: CachedSetlist | null, summary: SetlistSummary): boolean {
  return !cached || cached.updatedAt !== summary.updatedAt;
}

/** Тело `GET /api/setlists/:id`, каким его хранит `CachedSetlist.payload` —
 *  ровно те поля, что читает `wordsIdsOf`, а не полная форма ответа
 *  (`SetlistResult`, ../setlist.ts): типизируем здесь только то, что
 *  используем, вместо того чтобы тащить сюда зависимость от формы, которую
 *  вправе менять тот модуль. */
interface SetlistBodyLike {
  songs?: Array<{ words?: { id: string } | null } | null>;
}

/**
 * Id слов из состава одного сетлиста — вход для докачки слов
 * (`prefetchWords`, offline/prefetch.ts): предстоящий сетлист прочитан целиком
 * офлайн только тогда, когда офлайн читаются и слова каждой его песни, а не
 * только список названий. Песня без слов (`words: null` — аудио-only позиция
 * сетлиста, ../setlist.ts) не даёт ничего: качать для неё нечего.
 *
 * Принимает `unknown`, а не типизированный `SetlistBodyLike`: то, что лежит в
 * `CachedSetlist.payload`, тем же приёмом, что у `CachedWords.payload`
 * (offline-store.ts) — конверт `SHAPE` ручается только за форму ОБЁРТКИ, не
 * за форму значения внутри неё, поэтому чтение защищено руками (`Array.isArray`,
 * проверка типа `id`), а не слепым кастом.
 */
export function wordsIdsOf(setlistBody: unknown): string[] {
  const body = setlistBody as SetlistBodyLike | null | undefined;
  if (!body || !Array.isArray(body.songs)) return [];
  const ids: string[] = [];
  for (const song of body.songs) {
    const id = song?.words?.id;
    if (typeof id === 'string' && id) ids.push(id);
  }
  return ids;
}
